import { store } from '@/app/app-store';
import {
  ActivationModel,
  AddressInsuranceModel,
  ContactInsuranceModel,
  CustomerInsuranceModel,
  IndividualModel,
  PolicyModel,
} from '@/app/shared/models';
import { refsService as refs } from '@/app/shared/refs.service';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';

@Module({ store, name: 'policyModule', namespaced: true, dynamic: true })
export class PolicyModule extends VuexModule {
  public policy: PolicyModel = new PolicyModel();

  public activation: ActivationModel = new ActivationModel();

  public customer: CustomerInsuranceModel = new CustomerInsuranceModel();
  public address: AddressInsuranceModel = new AddressInsuranceModel();
  public email: ContactInsuranceModel = new ContactInsuranceModel();
  public phone: ContactInsuranceModel = new ContactInsuranceModel();
  public cellPhone: ContactInsuranceModel = new ContactInsuranceModel();

  public individual: IndividualModel = new IndividualModel();
  public individualAddress: AddressInsuranceModel = new AddressInsuranceModel();
  public individualEmail: ContactInsuranceModel = new ContactInsuranceModel();
  public individualPhone: ContactInsuranceModel = new ContactInsuranceModel();

  @Mutation
  public SET_POLICY(policy: PolicyModel) {
    this.policy = {
      ...policy,
      FinishDate: policy.FinishDate
        ? refs.moment(new Date(policy.FinishDate)).format('YYYY-MM-DD')
        : '',
    };
  }

  @Mutation
  public SET_ACTIVATION(activation: ActivationModel) {
    this.activation = activation;
  }

  @Mutation
  public SET_CUSTOMER(customer: CustomerInsuranceModel) {
    this.customer = {
      ...customer,
      Birthdate: customer.Birthdate
        ? refs.moment(new Date(customer.Birthdate)).format('YYYY-MM-DD')
        : '',
    };
  }

  @Mutation
  public SET_ADDRESS(address: AddressInsuranceModel) {
    this.address = address;
  }
  @Mutation
  public SET_EMAIL(email: ContactInsuranceModel) {
    this.email = email;
  }
  @Mutation
  public SET_PHONE(phone: ContactInsuranceModel) {
    this.phone = phone;
  }
  @Mutation
  public SET_CELLPHONE(cellPhone: ContactInsuranceModel) {
    this.cellPhone = cellPhone;
  }

  @Mutation
  public SET_INDIVIDUAL(individual: IndividualModel) {
    this.individual = individual;
  }
  @Mutation
  public SET_INDIVIDUAL_ADDRESS(individualAddress: AddressInsuranceModel) {
    this.individualAddress = individualAddress;
  }
  @Mutation
  public SET_INDIVIDUAL_EMAIL(individualEmail: ContactInsuranceModel) {
    this.individualEmail = individualEmail;
  }
  @Mutation
  public SET_INDIVIDUAL_PHONE(individualPhone: ContactInsuranceModel) {
    this.individualPhone = individualPhone;
  }

  @Action({ commit: 'SET_POLICY', rawError: true })
  public async loadPolicyById(policyId: string) {
    return await refs.services.policy.getPolicyById(policyId);
  }
  @Action({ rawError: true })
  public async updatePolicy(data: { policyId: string; policy: PolicyModel }) {
    await refs.services.policy.updatePolicy(data.policyId, {
      Id: data.policy.Id,
      Price: -50,
      Status: data.policy.Status,
      OurCode: data.policy.OurCode,
      FinishDate: data.policy.FinishDate,
    });
  }

  @Action({ commit: 'SET_CUSTOMER', rawError: true })
  public async loadCustomerByPolicyId(policyId: string) {
    return await refs.services.policy.getCustomerByPolicyId(policyId);
  }
  @Action({ rawError: true })
  public async updateCustomer(data: {
    policyId: string;
    customer: CustomerInsuranceModel;
    newAddress: boolean;
    newEmail: boolean;
    newPhone: boolean;
    newCellPhone: boolean;
  }) {
    if (this.address.PostalCode) {
      if (data.newAddress)
        await refs.modules.policy.createCustomerAddress({
          policyId: data.policyId,
          address: this.address,
        });
      else
        await refs.modules.policy.updateCustomerAddress({
          policyId: data.policyId,
          addressId: this.address.Id,
          address: this.address,
        });
    }
    if (this.email.Value) {
      if (data.newEmail)
        await refs.modules.policy.createCustomerContact({
          policyId: data.policyId,
          contact: this.email,
        });
      else
        await refs.modules.policy.updateCustomerContact({
          policyId: data.policyId,
          contactId: this.email.Id,
          contact: this.email,
        });
    }
    if (this.phone.Value) {
      if (data.newPhone)
        await refs.modules.policy.createCustomerContact({
          policyId: data.policyId,
          contact: this.phone,
        });
      else
        await refs.modules.policy.updateCustomerContact({
          policyId: data.policyId,
          contactId: this.phone.Id,
          contact: this.phone,
        });
    }
    if (this.cellPhone.Value) {
      if (data.newCellPhone)
        await refs.modules.policy.createCustomerContact({
          policyId: data.policyId,
          contact: this.cellPhone,
        });
      else
        await refs.modules.policy.updateCustomerContact({
          policyId: data.policyId,
          contactId: this.cellPhone.Id,
          contact: this.cellPhone,
        });
    }
    await refs.services.policy.updateCustomer(data.policyId, data.customer);
  }
  @Action({ rawError: true })
  public async createCustomerAddress(data: {
    policyId: string;
    address: AddressInsuranceModel;
  }) {
    await refs.services.policy.createCustomerAddress(
      data.policyId,
      data.address
    );
  }
  @Action({ rawError: true })
  public async updateCustomerAddress(data: {
    policyId: string;
    addressId: string;
    address: AddressInsuranceModel;
  }) {
    await refs.services.policy.updateCustomerAddress(
      data.policyId,
      data.addressId,
      data.address
    );
  }
  @Action({ rawError: true })
  public async createCustomerContact(data: {
    policyId: string;
    contact: ContactInsuranceModel;
  }) {
    await refs.services.policy.createCustomerContact(
      data.policyId,
      data.contact
    );
  }
  @Action({ rawError: true })
  public async updateCustomerContact(data: {
    policyId: string;
    contactId: string;
    contact: ContactInsuranceModel;
  }) {
    await refs.services.policy.updateCustomerContact(
      data.policyId,
      data.contactId,
      data.contact
    );
  }

  @Action({ rawError: true })
  public async createIndividual(data: {
    policyId: string;
    activationId: string;
    individual: IndividualModel;
  }) {
    const { Id } = await refs.services.policy.createIndividual(
      data.policyId,
      data.activationId,
      {
        Id: data.individual.Id,
        Name: data.individual.Name,
        Document: data.individual.Document,
        DocumentType: data.individual.DocumentType,
        Birthdate: data.individual.Birthdate,
        Gender: data.individual.Gender,
        Contacts: data.individual.Contacts,
        Addresses: data.individual.Addresses,
        Type: 3,
      }
    );
    if (this.individualAddress.PostalCode)
      await refs.modules.policy.createIndividualAddress({
        policyId: data.policyId,
        activationId: data.activationId,
        individualId: Id,
        address: this.individualAddress,
      });
    if (this.individualEmail.Value)
      await refs.modules.policy.createIndividualContact({
        policyId: data.policyId,
        activationId: data.activationId,
        individualId: Id,
        contact: this.individualEmail,
      });
    if (this.individualPhone.Value)
      await refs.modules.policy.createIndividualContact({
        policyId: data.policyId,
        activationId: data.activationId,
        individualId: Id,
        contact: this.individualPhone,
      });
  }
  @Action({ rawError: true })
  public async updateIndividual(data: {
    policyId: string;
    activationId: string;
    individualId: string;
    individual: IndividualModel;
    newAddress: boolean;
    newEmail: boolean;
    newPhone: boolean;
  }) {
    if (this.individualAddress.PostalCode) {
      if (data.newAddress)
        await refs.modules.policy.createIndividualAddress({
          policyId: data.policyId,
          activationId: data.activationId,
          individualId: data.individualId,
          address: this.individualAddress,
        });
      else
        await refs.modules.policy.updateIndividualAddress({
          policyId: data.policyId,
          activationId: data.activationId,
          individualId: data.individualId,
          addressId: this.individualAddress.Id,
          address: this.individualAddress,
        });
    }
    if (this.individualEmail.Value) {
      if (data.newEmail)
        await refs.modules.policy.createIndividualContact({
          policyId: data.policyId,
          activationId: data.activationId,
          individualId: data.individualId,
          contact: this.individualEmail,
        });
      else
        await refs.modules.policy.updateIndividualContact({
          policyId: data.policyId,
          activationId: data.activationId,
          individualId: data.individualId,
          contactId: this.individualEmail.Id,
          contact: this.individualEmail,
        });
    }
    if (this.individualPhone.Value) {
      if (data.newPhone)
        await refs.modules.policy.createIndividualContact({
          policyId: data.policyId,
          activationId: data.activationId,
          individualId: data.individualId,
          contact: this.individualPhone,
        });
      else
        await refs.modules.policy.updateIndividualContact({
          policyId: data.policyId,
          activationId: data.activationId,
          individualId: data.individualId,
          contactId: this.individualPhone.Id,
          contact: this.individualPhone,
        });
    }
    await refs.services.policy.updateIndividual(
      data.policyId,
      data.activationId,
      data.individualId,
      {
        Id: data.individual.Id,
        Name: data.individual.Name,
        Document: data.individual.Document,
        DocumentType: data.individual.DocumentType,
        Birthdate: data.individual.Birthdate,
        Gender: data.individual.Gender,
        Contacts: data.individual.Contacts,
        Addresses: data.individual.Addresses,
        Type: 3,
      }
    );
  }
  @Action({ rawError: true })
  public async createIndividualAddress(data: {
    policyId: string;
    activationId: string;
    individualId: string;
    address: AddressInsuranceModel;
  }) {
    await refs.services.policy.createIndividualAddress(
      data.policyId,
      data.activationId,
      data.individualId,
      data.address
    );
  }
  @Action({ rawError: true })
  public async updateIndividualAddress(data: {
    policyId: string;
    activationId: string;
    individualId: string;
    addressId: string;
    address: AddressInsuranceModel;
  }) {
    await refs.services.policy.updateIndividualAddress(
      data.policyId,
      data.activationId,
      data.individualId,
      data.addressId,
      data.address
    );
  }
  @Action({ rawError: true })
  public async createIndividualContact(data: {
    policyId: string;
    activationId: string;
    individualId: string;
    contact: ContactInsuranceModel;
  }) {
    await refs.services.policy.createIndividualContact(
      data.policyId,
      data.activationId,
      data.individualId,
      data.contact
    );
  }
  @Action({ rawError: true })
  public async updateIndividualContact(data: {
    policyId: string;
    activationId: string;
    individualId: string;
    contactId: string;
    contact: ContactInsuranceModel;
  }) {
    await refs.services.policy.updateIndividualContact(
      data.policyId,
      data.activationId,
      data.individualId,
      data.contactId,
      data.contact
    );
  }

  @Action({ rawError: true })
  public async resendCertificateByPolicyId(data: {
    policyId: string;
    payload: object;
  }) {
    await refs.services.policy.resendCertificateByPolicyId(
      data.policyId,
      data.payload
    );
  }
}
